import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { clearMeetingOutComeDetails } from "./meetingoutcomeSlice";
import {
  createMeetingOutCome,
  getAllMeetingOutCome,
  getMeetingOutComeByID,
  updateMeetingOutCome,
} from "./services/meetingoutcome.service";

const CreateMeetingOutCome: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const MeetingOutComeDetail = useAppSelector(
    (state) => state.meetingoutcome.MeetingOutComeDetail
  );
  const meeting_outcome_guid = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.meetingoutcome.formLoading
  );

  useEffect(() => {
    return () => {
      dispatch(clearMeetingOutComeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (meeting_outcome_guid) {
      dispatch(getMeetingOutComeByID(meeting_outcome_guid));
    }
  }, [meeting_outcome_guid]);

  const handleSubmit = async (values: any) => {
    try {
      if (!meeting_outcome_guid) {
        const insertPayload = {
          meeting_outcome: values.meeting_outcome,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createMeetingOutCome(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMeetingOutComeDetails());
          dispatch(getAllMeetingOutCome());
        }
      } else {
        const editPayload = {
          meeting_outcome_guid: meeting_outcome_guid,
          meeting_outcome: values.meeting_outcome,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateMeetingOutCome(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMeetingOutComeDetails());
          dispatch(getAllMeetingOutCome());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={MeetingOutComeDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {meeting_outcome_guid
                  ? "Update Meeting Outcome"
                  : "Create Meeting Outcome"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="meeting_outcome"
                label="Meeting Outcome"
                placeholder="Meeting Outcome"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"isactive"}
                component={FormSelectionField}
                label={"Status"}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={meeting_outcome_guid ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateMeetingOutCome;
